import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ReactComponent as Search } from '$icons/SearchIcon.svg';
import { ReactComponent as Arrow } from '$icons/uncolored/arrow.svg';
import { isValidNumber } from '$utils/isValidNumber';
import { useTranslation } from 'react-i18next';
import SPKCandidateHiddenSkills from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateHiddenSkills';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import Spinner from '../../Spinner/Spinner';
import ActiveValues from '../../../constants/ActiveValues';
import {
  LoadOnePositionOutput,
  useLoadOnePositionQuery,
} from '../../../store/modules/companies';
import SPKEmployerCandidatesList from './SPKEmployerCandidatesList';
import { CandidateData } from '../../../types/candidates';
import { filterNames } from '../../../constants/FiltersNames';
import SearchFilter from '../utils/SearchFilter';
import GetCandidateDataByMultipleFetch from '../utils/GetCandidateDataByMultipleFetch';
import { SPKPositionStatusSwitch } from './SPKPositionStatusSwitch/SPKPositionStatusSwitch';

const SPKCandidatesPositionPage = () => {
  const MAX_DISPLAYED_SKILLS = 4;

  const { t } = useTranslation('global');
  const { positionId } = useParams();
  const [candidates, setCandidates] = useState<CandidateData[]>([]);
  const [showDescription, setShowDescription] = useState(false);
  const [filteredCandidates, setFilteredCandidates] = useState<CandidateData[]>(
    []
  );
  const [candidatesPositionCount, setCandidatesPositionCount] = useState({
    new: 0,
    shortlisted: 0,
    declined: 0,
    requestInterview: 0,
    hired: 0,
    offerSent: 0,
    all: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [switchingCandidateId, setSwitchingCandidateId] = useState<
    number | null
  >(null);
  const { register, watch } = useForm({
    defaultValues: {
      searchInput: '',
    },
    mode: 'onChange',
  });

  const searchValue = watch('searchInput');

  const { data: position, isLoading: isLoadingPosition } =
    useLoadOnePositionQuery({
      id: positionId,
    });

  const cps = (searchParams.get('cps') ?? ActiveValues.New) as ActiveValues;

  const filters = useMemo(
    () => ({
      cps,
      matchSkill: searchParams.get(filterNames.skillsMatchs) ?? 'ALL',
      matchAdditionalTags:
        searchParams.get(filterNames.additionalTagsMatch) ?? 'ALL',

      positionId: parseInt(positionId ?? '0', 10),
      id:
        parseInt(searchParams.get(filterNames.id) ?? '0', 10) === 0
          ? null
          : parseInt(searchParams.get(filterNames.id) ?? '0', 10),

      query: searchParams.get(filterNames.query) ?? '',
      name: searchParams.get('name') ?? null,
    }),
    [cps, positionId, searchParams]
  );

  const { data, isLoading } = GetCandidateDataByMultipleFetch(position);

  useEffect(() => {
    if (data && !isLoading) {
      setCandidates((prev) => {
        // Added new candidates
        const newCandidates = data.filter(
          (item: any) => !prev.some((prevItem) => prevItem.Id === item.Id)
        );

        // Changed status
        const parsePrev = prev.filter((item) => {
          /** Fix when you request interview on request interview tab, avoids disappearing the card */
          if (item.Id === switchingCandidateId) {
            const hasChangedStatus = data.some(
              (singleData: LoadOnePositionOutput) => {
                return (
                  singleData.Id === switchingCandidateId &&
                  singleData?.CandidatesPositionStatus?.[0]?.Status !==
                    item?.CandidatesPositionStatus?.[0]?.Status
                );
              }
            );
            if (!hasChangedStatus) return true;
          }
          /** End fix */
          return item.Id !== switchingCandidateId;
        });

        return [...parsePrev, ...newCandidates];
      });
    }
  }, [data, isLoading, switchingCandidateId]);

  useEffect(() => {
    if (position && position.CandidatesPositionStatus) {
      const candidatesCount = position?.CandidatesPositionStatus?.reduce(
        (acc, curr) => {
          acc[curr.Status] += 1;

          if (curr.Status !== 'declined') {
            acc.all += 1;
          }

          return acc;
        },
        {
          new: 0,
          shortlisted: 0,
          declined: 0,
          requestInterview: 0,
          hired: 0,
          offerSent: 0,
          all: 0,
        }
      );
      setCandidatesPositionCount(candidatesCount);
    }
  }, [position?.CandidatesPositionStatus, position]);

  const applyFilters = useMemo(() => {
    return SearchFilter(filters, candidates);
  }, [filters, candidates]);

  useEffect(() => {
    setFilteredCandidates(applyFilters);
  }, [applyFilters]);

  useEffect(() => {
    const inputSearchParams = new URLSearchParams(searchParams);
    if (searchValue) {
      if (isValidNumber(searchValue)) {
        inputSearchParams.set('Id', searchValue);
      } else if (searchValue.indexOf('@') >= 0) {
        inputSearchParams.set('Email', searchValue);
      } else {
        inputSearchParams.set('Query', searchValue);
      }
    } else {
      inputSearchParams.delete('Id');
      inputSearchParams.delete('Email');
      inputSearchParams.delete('Query');
    }
    setSearchParams(inputSearchParams);
  }, [searchParams, searchValue, setSearchParams]);

  return (
    <div className="!font-mulish" data-testid="SPKCandidatePositionPage">
      <div className="container px-4 lg:px-0">
        {!isLoadingPosition && (
          <>
            <div className="flex flex-col lg:flex-row pt-8 mb-2">
              <div className="flex flex-col">
                <div className="flex flex-row items-center gap-2 text-spk-mediumContrast text-xs">
                  <Link to="/employer-dashboard/positions">
                    {t('PositionCard.Positions')}
                  </Link>
                  <span className="text-sm">&gt;</span>
                  <span>{`${position?.JobPosition.title}`}</span>
                </div>
                <button
                  type="button"
                  className="flex flex-row gap-4 items-center"
                  onClick={() => setShowDescription((prev) => !prev)}
                >
                  <span className="text-spk-highContrast font-bold text-xl">{`${position?.JobPosition.title}`}</span>
                  <Arrow
                    className={`fill-spk-brightBlue w-4 h-4 transition-all duration-150 ${
                      showDescription ? 'rotate-180' : 'rotate-0'
                    }`}
                  />
                </button>
              </div>
              <div className="flex flex-row ml-auto items-center max-w-full">
                <div className="ml-auto flex flex-row bg-white items-center gap-2 px-2 rounded-xl border border-spk-neutral-40 text-spk-mediumContrast h-12">
                  <Search />
                  <input
                    type="text"
                    className="h-full outline-none placeholder:text-spk-mediumContrast max-w-44 w-full"
                    placeholder="Search"
                    {...register('searchInput')}
                  />
                </div>
                <div className="border-l border-spk-neutral-40 ml-4 pl-4 flex flex-row items-center self-center overflow-hidden max-w-full">
                  <SPKPositionStatusSwitch
                    value={(cps as ActiveValues) ?? ActiveValues.New}
                    setSwitchingId={setSwitchingCandidateId}
                    candidatesCount={candidatesPositionCount}
                  />
                </div>
              </div>
            </div>
            <div
              className={`text-sm flex gap-y-4 flex-col overflow-hidden transition-all duration-500 ease-linear ${
                showDescription ? 'max-h-36 py-2' : 'max-h-0'
              }`}
            >
              <div className="flex flex-row divide-x divide-spk-neutral-40 *:px-2 -mx-2 ">
                <div className="flex gap-2">
                  <span className="text-spk-mediumContrast">
                    {t('PositionCard.Experience')}
                  </span>
                  <span className="text-spk-highContrast">
                    {position?.ExperienceYears} {t('PositionCard.year')}
                    {position?.ExperienceYears &&
                      position?.ExperienceYears > 1 &&
                      's'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="text-spk-mediumContrast">
                    {t('PositionCard.USDMonthlyBudget')}:{' '}
                  </span>
                  <span className="text-spk-highContrast">
                    ${position?.SalaryLowerBound} - $
                    {position?.SalaryUpperBound}
                  </span>
                </div>
              </div>
              <div className="flex flex-row items-center">
                <span className="text-spk-mediumContrast mr-2">
                  {t('PositionCard.SkillsetOverview')}:
                </span>
                <div className="flex flex-row gap-2 items-center">
                  {position?.Skills_position.slice(0, MAX_DISPLAYED_SKILLS).map(
                    (item) => (
                      <span
                        key={`positionSkill_${item.Id}`}
                        className="rounded text-spk-mediumContrast border border-spk-neutral-40 p-1 text-xs"
                      >
                        {item.Title}
                      </span>
                    )
                  )}
                  {position?.Skills_position &&
                    position?.Skills_position.length > MAX_DISPLAYED_SKILLS && (
                      <SPKCandidateHiddenSkills
                        hiddenSkills={position?.Skills_position.slice(
                          MAX_DISPLAYED_SKILLS,
                          -1
                        ).map((singleSkill) => ({
                          Id: singleSkill.Id,
                          Skill: { title: singleSkill.Title },
                        }))}
                        uniqueId={position.Id}
                      />
                    )}
                </div>
              </div>
            </div>
          </>
        )}
        {isLoading ? (
          <div>
            <Spinner height="100%" width="100%" />
          </div>
        ) : (
          <SPKEmployerCandidatesList
            activeValue={cps}
            candidates={filteredCandidates}
            setSwitchingId={setSwitchingCandidateId}
            position={position as PositionInternalDto}
          />
        )}
      </div>
    </div>
  );
};

export default SPKCandidatesPositionPage;
